import { format as dateFnsFormat } from "date-fns/format";
import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { i18n } from "@tellescope/i18n";
import { getDateFnsLocale } from "./get-date-fns-locale";

/** Shift a Date so that when formatted in local time it displays the values seen in the target timezone. */
export function shiftToTimezone(date: Date, timeZone?: string): Date {
  if (!timeZone) return date;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)!.value);
  let hour = get("hour");
  if (hour === 24) hour = 0;
  return new Date(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
}

export type DateFormat =
  | "MMM D"
  | "MMM D, YYYY"
  | "MMMM D, YYYY"
  | "ddd, MMM D"
  | "ddd, MMM D, YYYY"
  | "ddd, MMMM D, YYYY"
  | "dddd, MMM D"
  | "dddd, MMM D, YYYY"
  | "dddd, MMMM D, YYYY"
  | "MM/DD/YYYY"
  | "MM/DD/YY"
  | "MM-DD-YYYY"
  | "DD-MM-YYYY"
  | "MM-DD-YY"
  | "DD-MM-YY";
export type TimeFormat = "hh:mm a" | "hh:mm:ss a" | "HH:mm" | "HH:mm:ss";

// Template literal type that generates all valid date-time format combinations
export type DateTimeFormat = `${DateFormat} at ${TimeFormat}`;

export type RelativeOptions = {
  unit: "days" | "weeks" | "months" | "years";
  amount: number;
  direction: "future" | "past" | "both";
  approximate?: boolean | "weeks" | "months" | "years";
};

// Extract date format from a datetime format string
type ExtractDateFormat<T extends DateTimeFormat> = T extends `${infer D extends DateFormat} at ${string}` ? D : never;

// Extract time format from a datetime format string
type ExtractTimeFormat<T extends DateTimeFormat> = T extends `${string} at ${infer Time extends TimeFormat}`
  ? Time
  : never;

const DATE_FORMAT_TOKENS: Record<DateFormat, string> = {
  "MMM D": "MMM d",
  "MMM D, YYYY": "MMM d, yyyy",
  "MMMM D, YYYY": "MMMM d, yyyy",
  "ddd, MMM D": "EEE, MMM d",
  "ddd, MMM D, YYYY": "EEE, MMM d, yyyy",
  "ddd, MMMM D, YYYY": "EEE, MMMM d, yyyy",
  "dddd, MMM D": "EEEE, MMM d",
  "dddd, MMM D, YYYY": "EEEE, MMM d, yyyy",
  "dddd, MMMM D, YYYY": "EEEE, MMMM d, yyyy",
  "MM/DD/YYYY": "MM/dd/yyyy",
  "MM/DD/YY": "MM/dd/yy",
  "MM-DD-YYYY": "MM-dd-yyyy",
  "DD-MM-YYYY": "dd-MM-yyyy",
  "MM-DD-YY": "MM-dd-yy",
  "DD-MM-YY": "dd-MM-yy",
};

const TIME_FORMAT_TOKENS: Record<TimeFormat, string> = {
  "hh:mm a": "h:mm a",
  "hh:mm:ss a": "h:mm:ss a",
  "HH:mm": "HH:mm",
  "HH:mm:ss": "HH:mm:ss",
};

/**
 * Formats a date using the specified format string with optional relative formatting.
 *
 * @param date - The date to format (Date object, string, or timestamp number)
 * @param format - The date format to use. Defaults to "MMM D, YYYY" (e.g., "Jan 15, 2024")
 * @param relative - Controls relative date formatting (true | "always" | RelativeOptions)
 * @param timezone - Optional IANA timezone string (e.g., "America/New_York"). Defaults to the user's local timezone.
 * @param locale - Optional language code for localized month names and i18n strings. Defaults to i18n.language.
 * @returns The formatted date string or relative term
 *
 * @deprecated - Please use formatter functions from date-fns instead
 */
export function formatDate(
  date: Date | string | number,
  format: DateFormat = "MMM D, YYYY",
  relative?: boolean | "always" | RelativeOptions,
  timezone?: string,
  locale?: string
): string {
  try {
    const dateObj = new Date(date);
    const lng = locale ?? i18n.language;
    const dateFnsLocale = getDateFnsLocale(lng);

    if (relative) {
      const nowShifted = shiftToTimezone(new Date(), timezone);
      const targetShifted = shiftToTimezone(dateObj, timezone);
      const today = new Date(nowShifted.getFullYear(), nowShifted.getMonth(), nowShifted.getDate());
      const targetDate = new Date(targetShifted.getFullYear(), targetShifted.getMonth(), targetShifted.getDate());

      const diffInDays = differenceInCalendarDays(targetDate, today);

      if (diffInDays === 0) return i18n.t("Today", { ns: "dates", lng });
      if (diffInDays === 1) return i18n.t("Tomorrow", { ns: "dates", lng });
      if (diffInDays === -1) return i18n.t("Yesterday", { ns: "dates", lng });

      const relativeOptions: RelativeOptions | null =
        relative === "always"
          ? { unit: "years", amount: Infinity, direction: "both", approximate: false }
          : typeof relative === "object"
          ? relative
          : null;

      if (relativeOptions) {
        const { unit, amount, direction, approximate = false } = relativeOptions;
        const absDiff = Math.abs(diffInDays);
        const isFuture = diffInDays > 0;

        const maxDays = (() => {
          if (amount === Infinity) return Infinity;
          switch (unit) {
            case "days":
              return amount;
            case "weeks":
              return amount * 7;
            case "months":
              return amount * 30;
            case "years":
              return amount * 365;
          }
        })();

        const shouldUseRelative = (() => {
          if (direction === "both") return absDiff <= maxDays;
          if (direction === "future") return isFuture && diffInDays <= maxDays;
          if (direction === "past") return !isFuture && absDiff <= maxDays;
          return false;
        })();

        if (shouldUseRelative) {
          const shouldUseAboutForDays = relative === "always" || approximate === true;
          const shouldUseAboutForWeeks = relative === "always" || approximate === true || approximate === "weeks";
          const shouldUseAboutForMonths =
            relative === "always" || approximate === true || approximate === "weeks" || approximate === "months";
          const shouldUseAboutForYears =
            relative === "always" ||
            approximate === true ||
            approximate === "weeks" ||
            approximate === "months" ||
            approximate === "years";

          if (absDiff < 7) {
            if (shouldUseAboutForDays) {
              return isFuture
                ? i18n.t("about {{count}} days from now", { ns: "dates", count: absDiff, lng })
                : i18n.t("about {{count}} days ago", { ns: "dates", count: absDiff, lng });
            }
            return isFuture
              ? i18n.t("{{count}} days from now", { ns: "dates", count: absDiff, lng })
              : i18n.t("{{count}} days ago", { ns: "dates", count: absDiff, lng });
          }
          if (absDiff < 30) {
            const weeks = Math.round(absDiff / 7);
            if (shouldUseAboutForWeeks) {
              return isFuture
                ? i18n.t("about {{count}} weeks from now", { ns: "dates", count: weeks, lng })
                : i18n.t("about {{count}} weeks ago", { ns: "dates", count: weeks, lng });
            }
            return isFuture
              ? i18n.t("{{count}} weeks from now", { ns: "dates", count: weeks, lng })
              : i18n.t("{{count}} weeks ago", { ns: "dates", count: weeks, lng });
          }
          if (absDiff < 365) {
            const months = Math.round(absDiff / 30);
            if (shouldUseAboutForMonths) {
              return isFuture
                ? i18n.t("about {{count}} months from now", { ns: "dates", count: months, lng })
                : i18n.t("about {{count}} months ago", { ns: "dates", count: months, lng });
            }
            return isFuture
              ? i18n.t("{{count}} months from now", { ns: "dates", count: months, lng })
              : i18n.t("{{count}} months ago", { ns: "dates", count: months, lng });
          }
          const years = Math.round(absDiff / 365);
          if (shouldUseAboutForYears) {
            return isFuture
              ? i18n.t("about {{count}} years from now", { ns: "dates", count: years, lng })
              : i18n.t("about {{count}} years ago", { ns: "dates", count: years, lng });
          }
          return isFuture
            ? i18n.t("{{count}} years from now", { ns: "dates", count: years, lng })
            : i18n.t("{{count}} years ago", { ns: "dates", count: years, lng });
        }
      }
    }

    const shifted = shiftToTimezone(dateObj, timezone);
    return dateFnsFormat(shifted, DATE_FORMAT_TOKENS[format], { locale: dateFnsLocale });
  } catch (err) {
    console.error(err);
    return "";
  }
}

/**
 * Formats the time portion of a date using the specified time format.
 *
 * @param date - The date to extract and format the time from (Date object, string, or timestamp number)
 * @param format - The time format to use. Defaults to "hh:mm a" (12-hour format with AM/PM)
 * @param timezone - Optional IANA timezone string (e.g., "America/New_York"). Defaults to the user's local timezone.
 * @param locale - Optional language code for localized AM/PM markers. Defaults to i18n.language.
 * @returns The formatted time string
 */
export function formatTime(
  date: Date | string | number,
  format: TimeFormat = "hh:mm a",
  timezone?: string,
  locale?: string
): string {
  try {
    const dateObj = new Date(date);
    const shifted = shiftToTimezone(dateObj, timezone);
    const dateFnsLocale = getDateFnsLocale(locale ?? i18n.language);
    return dateFnsFormat(shifted, TIME_FORMAT_TOKENS[format], { locale: dateFnsLocale });
  } catch (err) {
    console.error(err);
    return "";
  }
}

/**
 * Formats a date and time together using a combined format string, with support for date-only, time-only,
 * or full datetime formats. Includes optional relative date formatting.
 *
 * @param date - The date to format (Date object, string, or timestamp number)
 * @param format - The format string to use (DateTimeFormat, DateFormat, or TimeFormat)
 * @param relative - Controls relative date formatting (true | "always" | RelativeOptions)
 * @param timezone - Optional IANA timezone string
 * @param locale - Optional language code. Defaults to i18n.language.
 * @returns The formatted datetime string
 */
export function formatDateTime<T extends DateTimeFormat>(
  date: Date | string | number,
  format: T | DateFormat | TimeFormat = "MMM D, YYYY at hh:mm a" as T,
  relative: boolean | "always" | RelativeOptions = false,
  timezone?: string,
  locale?: string
): string {
  try {
    const dateObj = new Date(date);
    const lng = locale ?? i18n.language;

    if ((format as string).includes(":") && !(format as string).includes(" at ")) {
      return formatTime(dateObj, format as TimeFormat, timezone, locale);
    }

    if (!(format as string).includes(" at ")) {
      return formatDate(dateObj, format as DateFormat, relative, timezone, locale);
    }

    const [dateFormat, timeFormat] = format.split(" at ") as [ExtractDateFormat<T>, ExtractTimeFormat<T>];

    const datePart = formatDate(dateObj, dateFormat, relative, timezone, locale);

    if (
      relative &&
      (datePart === i18n.t("Today", { ns: "dates", lng }) ||
        datePart === i18n.t("Tomorrow", { ns: "dates", lng }) ||
        datePart === i18n.t("Yesterday", { ns: "dates", lng }))
    ) {
      const timePart = formatTime(dateObj, timeFormat, timezone, locale);
      return i18n.t(`{{datePart}} at {{timePart}}`, { ns: "dates", datePart, timePart, lng });
    }

    if (relative === "always") {
      if (datePart.includes("ago") || datePart.includes("from now")) {
        return datePart;
      }
    } else if (typeof relative === "object") {
      const isRelativeText =
        datePart.includes("ago") ||
        datePart.includes("from now") ||
        datePart === i18n.t("Today", { ns: "dates", lng }) ||
        datePart === i18n.t("Tomorrow", { ns: "dates", lng }) ||
        datePart === i18n.t("Yesterday", { ns: "dates", lng });

      if (isRelativeText) {
        const timePart = formatTime(dateObj, timeFormat, timezone, locale);
        return i18n.t(`{{datePart}} at {{timePart}}`, { ns: "dates", datePart, timePart, lng });
      }
    }

    const timePart = formatTime(dateObj, timeFormat, timezone, locale);
    return i18n.t(`{{datePart}} at {{timePart}}`, { ns: "dates", datePart, timePart, lng });
  } catch (err) {
    console.error(err);
    return "";
  }
}

/**
 * Validates whether a given input can be parsed as a valid date.
 */
export function isValidDate(date?: Date | string | number | "") {
  if (!date) {
    return false;
  }

  try {
    const dateObj = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
    return !isNaN(dateObj.getTime());
  } catch {
    return false;
  }
}
